// Laravel Echo + Reverb client for realtime notifications (Laravel platform API).

import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { envConfig } from "../utils/config";
import { handleIncomingNotification } from "./notification.helpers";

declare global {
    interface Window {
        Pusher: typeof Pusher;
    }
}

const NEW_NOTIFICATION_EVENT = ".NEW_NOTIFICATION";
const GLOBAL_CHANNEL = "notifications.global";

const createNotificationEcho = () => {
    let echo: Echo<"reverb"> | null = null;
    let subscribedUserId: number | null = null;

    const getBroadcastingAuthUrl = (): string =>
        `${String(envConfig.APP_PLATFORM_API_URL).replace(/\/$/, "")}/broadcasting/auth`;

    const buildEcho = (sanctumToken: string): Echo<"reverb"> => {
        window.Pusher = Pusher;

        const port = Number(envConfig.REVERB_PORT || 8080);

        return new Echo({
            broadcaster: "reverb",
            key: envConfig.REVERB_APP_KEY,
            wsHost: envConfig.REVERB_HOST,
            wsPort: port,
            wssPort: port,
            forceTLS: envConfig.REVERB_SCHEME === "https",
            enabledTransports: ["ws", "wss"],
            authEndpoint: getBroadcastingAuthUrl(),
            auth: {
                headers: {
                    Authorization: `Bearer ${sanctumToken}`,
                    Accept: "application/json",
                },
            },
        });
    };

    const subscribeChannels = (instance: Echo<"reverb">, numericUserId: number): void => {
        instance
            .private(`notifications.${numericUserId}`)
            .listen(NEW_NOTIFICATION_EVENT, handleIncomingNotification)
            .error((error: unknown) => {
                if (import.meta.env.DEV) {
                    console.warn("[Reverb] private channel subscription failed", error);
                }
            });

        instance
            .channel(GLOBAL_CHANNEL)
            .listen(NEW_NOTIFICATION_EVENT, handleIncomingNotification);
    };

    const connect = (sanctumToken: string, numericUserId: number): void => {
        if (!sanctumToken || !numericUserId) {
            return;
        }

        if (
            echo &&
            subscribedUserId === numericUserId &&
            echo.connector?.pusher?.connection?.state === "connected"
        ) {
            return;
        }

        disconnect();

        echo = buildEcho(sanctumToken);
        subscribedUserId = numericUserId;
        subscribeChannels(echo, numericUserId);
    };

    const disconnect = (): void => {
        if (!echo) {
            subscribedUserId = null;
            return;
        }

        if (subscribedUserId !== null) {
            echo.leave(`notifications.${subscribedUserId}`);
        }

        echo.leave(GLOBAL_CHANNEL);
        echo.disconnect();
        echo = null;
        subscribedUserId = null;
    };

    const reconnect = (sanctumToken: string, numericUserId: number): void => {
        disconnect();
        connect(sanctumToken, numericUserId);
    };

    const getConnectionStatus = (): string => {
        const state = echo?.connector?.pusher?.connection?.state;

        if (!state) {
            return "Not connected";
        }

        return state.charAt(0).toUpperCase() + state.slice(1);
    };

    return {
        connect,
        disconnect,
        reconnect,
        getConnectionStatus,
    };
};

export const notificationEcho = createNotificationEcho();
